import { notification } from "antd";
import dayjs from "dayjs";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
    getApiError,
    getKpiGetMarketProgressThunk,
    getPlanGetByMarketIdThunk,
    getPriceListByMarketThunk,
} from "tm-shared/api";
import { getStorageInventoryGetThunk } from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { PaymentForm, Product, TaskSaleAdditional } from "tm-shared/types";

import PlanIcon from "@/assets/icons/plan-icon.svg?react";
import Header from "@/components/Header";
import Meta from "@/components/Meta";
import { SVScanButton } from "@/components/SVScanButton";
import { HeaderFilterButton } from "@/components/UI/HeaderFilterButton";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useQueryFilter } from "@/hooks/useQueryFilter";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { ProductsFormCatalog } from "@/pages/WorkPlan/Tasks/SalePage/components/ProductsFormCatalog";
import { setSalePaymentForm } from "@/store/slices/progress";
import { setSaleQuery } from "@/store/slices/ui";

import { OfflineFilterDrawer } from "../components/OfflineFilterDrawer";
import {
    StyledFormWarning,
    StyledSum,
    StyledTypographyFormWarning,
    StyledTypographySum,
} from "../shared";
import { SaleFiltersPanel } from "./components/SaleFiltersPanel";
import {
    MainWrapper,
    StyledPlanButton,
    StyledPromoLink,
} from "./SalePage.styled";
import {
    calculateOrderSum,
    checkIsStatisticNotBeforeMotivation,
    countActiveFilters,
    makeAdvancedProducts,
} from "./utils";
import { checkIsMML } from "./utils/checkIsMML";
import { makePlans } from "./utils/makePlans";

interface SalePageProps {
    marketId: string;
    task: TaskSaleAdditional;
}

export const SalePage: FC<SalePageProps> = ({ marketId, task }) => {
    const dispatch = useAppDispatch();

    const {
        offlineMode,
        products,
        marketsPriceLists,
        priceLists,
        plans,
        motivationInfoDate,
        inventoryList,
        facts,
    } = useAppSelector((state) => state.offline);

    const taskId = task.id;

    const scrollToTop = useScrollToTop("#catalog-container");

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [isPaymentFormTouched, setIsPaymentFormTouched] = useState(false);
    const [isOnlyPlanMode, setIsOnlyPlanMode] = useState(false);
    const [isPriceListLoading, setIsPriceListLoading] = useState(false);

    const [sortedAndFilteredProducts, setSortedAndFilteredProducts] = useState<
        Product[]
    >(() => Object.values(products));

    const { selectedFilters, hideZeroProducts, showOnlyMML } = useAppSelector(
        (s) => s.ui.productsFilter,
    );
    const { saleAdditional } = useAppSelector((state) => state.progress);

    const priceListId = marketsPriceLists?.[marketId] ?? "";
    const priceList = useMemo(
        () => priceLists?.[priceListId] ?? {},
        [priceListId, priceLists],
    );

    const currentPayload = saleAdditional[taskId];
    const currentProducts = useMemo(
        () => currentPayload?.products ?? {},
        [currentPayload?.products],
    );
    const currentPaymentForm = currentPayload?.paymentForm;
    const currentIgnoreRemainings = currentPayload?.ignoreRemainings ?? [];

    const { query: searchQuery } = useAppSelector(
        (state) => state.ui.saleAdditional,
    );

    const { markets, mmlStatistics, motivation } = useAppSelector(
        (state) => state.work,
    );

    const currentMarket = markets[marketId];
    const storageId = currentMarket?.storage?.id ?? "";
    const currentMarketInventory = inventoryList?.[storageId] || {};

    const searchFilteredProducts = useQueryFilter<Product>(
        sortedAndFilteredProducts,
        searchQuery,
        [
            (p) => p?.name,
            (p) => p?.barcode,
            (p) => p?.params?.description?.value,
            (p) => p?.params?.capacity?.value,
        ],
    );

    const currentMarketPlan = useMemo(
        () => makePlans(plans[marketId] || {}),
        [plans, marketId],
    );

    const currentRoutePlan = useMemo(
        () => makePlans(plans["unassignedPlan"] || {}),
        [plans],
    );

    const mmlList = useMemo(() => {
        const isStatisticValid = checkIsStatisticNotBeforeMotivation(
            motivation?.targetDate,
            motivationInfoDate,
        );
        const marketProgress = facts?.[marketId];

        return Object.keys(products).reduce(
            (acc, barcode) => {
                acc[barcode] = checkIsMML(
                    currentMarketPlan,
                    barcode,
                    isStatisticValid,
                    marketProgress,
                );
                return acc;
            },
            {} as Record<string, boolean>,
        );
    }, [
        products,
        currentMarketPlan,
        motivation?.targetDate,
        motivationInfoDate,
        mmlStatistics?.marketsProgress,
        marketId,
    ]);

    const productsWithMotivation = useMemo(() => {
        const specialMotivationGroups = Object.values(
            motivation?.motivationGroups || [],
        )
            .map((motivationGroup) => motivationGroup.group)
            .filter(({ groupType }) => groupType === "special");

        return makeAdvancedProducts(
            searchFilteredProducts,
            specialMotivationGroups,
            currentMarketPlan,
            currentRoutePlan,
            facts[marketId],
            priceList,
            mmlList,
        );
    }, [
        motivation?.motivationGroups,
        searchFilteredProducts,
        currentMarketPlan,
        currentRoutePlan,
        facts,
        marketId,
        priceList,
        mmlList,
    ]);

    const totalOrderSum = useMemo(
        () => calculateOrderSum(currentProducts),
        [currentProducts],
    );

    const activeFiltersCount = useMemo(
        () => countActiveFilters(selectedFilters),
        [selectedFilters],
    );

    const pageTitle = `Продаж ${currentMarket?.contragent?.name ?? ""}`;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSaleQuery(e.target.value));
    };

    const togglePlanFilter = useCallback(() => {
        setIsOnlyPlanMode((prev) => !prev);
        scrollToTop();
    }, [scrollToTop]);

    const handlePaymentFormChange = useCallback(
        (paymentForm: PaymentForm) => {
            if (!isPaymentFormTouched && currentPaymentForm) {
                setIsPaymentFormTouched(true);
            }

            dispatch(
                setSalePaymentForm({
                    taskId,
                    paymentForm,
                }),
            );
        },
        [isPaymentFormTouched, currentPaymentForm, dispatch, taskId],
    );

    useEffect(() => {
        if (!offlineMode && storageId) {
            dispatch(
                getStorageInventoryGetThunk({
                    storageId,
                }),
            );
            dispatch(
                getPlanGetByMarketIdThunk({
                    marketId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
            dispatch(
                getKpiGetMarketProgressThunk({
                    marketId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
        }
    }, [dispatch, marketId, storageId, offlineMode]);

    useEffect(() => {
        const getPriceList = async () => {
            try {
                setIsPriceListLoading(true);

                await dispatch(
                    getPriceListByMarketThunk({ marketId }),
                ).unwrap();
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    message: "Не вдалося завантажити ціни",
                    description: displayMessage,
                });
            } finally {
                setIsPriceListLoading(false);
            }
        };

        if (offlineMode || priceListId || Object.keys(priceList).length > 0) {
            return;
        }

        getPriceList();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, marketId]);

    return (
        <MainWrapper>
            <Meta title={pageTitle} />
            <Header
                title={pageTitle}
                back
                rightActions={
                    <>
                        <SVScanButton taskId={taskId} marketId={marketId} />

                        <StyledPromoLink to={"promo"}>%</StyledPromoLink>

                        <StyledPlanButton
                            type="text"
                            icon={<PlanIcon />}
                            onClick={togglePlanFilter}
                            $isPlanActive={isOnlyPlanMode}
                        />

                        <HeaderFilterButton
                            onClick={() => setIsFilterDrawerOpen(true)}
                            filterCount={activeFiltersCount}
                            active={hideZeroProducts || showOnlyMML}
                        />
                    </>
                }
            />

            {totalOrderSum > 0 && (
                <StyledSum>
                    <StyledTypographySum>
                        Сума: <strong>{totalOrderSum.toFixed(2)} грн.</strong>
                    </StyledTypographySum>
                </StyledSum>
            )}
            {isPaymentFormTouched && (
                <StyledFormWarning>
                    <StyledTypographyFormWarning>
                        Увага! Ви змінили форму оплати, продаж буде тільки по
                        ній
                    </StyledTypographyFormWarning>
                </StyledFormWarning>
            )}
            <SaleFiltersPanel
                query={searchQuery}
                onSearch={handleSearchChange}
                selectedForm={currentPaymentForm ?? null}
                onFormChange={handlePaymentFormChange}
                products={products}
                onFilteredChange={setSortedAndFilteredProducts}
            />

            <ProductsFormCatalog
                marketId={marketId}
                task={task}
                products={productsWithMotivation}
                isOnlyPlan={isOnlyPlanMode}
                loading={isPriceListLoading}
                payloadProducts={currentProducts}
                paymentForm={currentPaymentForm}
                ignoreRemainings={currentIgnoreRemainings}
            />

            <OfflineFilterDrawer
                open={isFilterDrawerOpen}
                setOpen={setIsFilterDrawerOpen}
                mmlList={mmlList}
                inventoryList={currentMarketInventory}
                setFilteredProducts={setSortedAndFilteredProducts}
            />
        </MainWrapper>
    );
};
